import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { Accordion, AccordionItem } from 'react-accessible-accordion';
// import { TCPNavigationStatuses } from 'consts/TCP';
import { useValidationComments } from 'hooks/useValidationComments';
import { Loader } from 'component/Loader';
import { REPORT_TYPES } from 'consts/ReportValidation';
import axios from 'axios';
// import { NavigationTabLinks } from '../../Tabs/NavigationLinks';
import { DMSBlockView } from 'pages/TaskClearance/Daily/Blocks/DMSBlock/DMSBlock';
import { FieldWorkBlockView } from 'pages/TaskClearance/Daily/Blocks/FieldWorkBlock/FieldWorkBlock';
import useProgressMap from 'hooks/useProgressMap';
import { TranslateWrapperComponent } from 'component/Translation';
import { ReactComponent as TranslateSvg } from 'assets/images/icons/translate_24px.svg';
import { ButtonComponent } from 'component/Button';
import { useUser } from 'routes/UserContext';
import { rejectWeeklyReport } from 'api/DailyApi';
import { getTCPFormById } from 'api/TCPApi';
import { getHAById } from 'api/HaApi';

export const WeeklyViewPage = () => {
  const { clearanceId, weeklyId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const [formData, setFormData] = useState(null);
  const [loader, setLoader] = useState(false);
  const [zone, setZone] = useState(null);
  const [haReportPolygon, setHaReportPolygon] = useState(null);
  const [crosshair, setCrosshair] = useState(null);
  const validationComments = useValidationComments(REPORT_TYPES.WEEKLY, weeklyId);

  const shouldLoadProgressMap = !!formData &&
    (formData?.info?.dms?.team_activity === 'field_work' ||
     formData?.info?.dms?.team_activity?.includes('field_work'));

  const [gridGeojson, mapLoader, setGridGeojson] = useProgressMap(
    clearanceId,
    null,
    weeklyId,
    shouldLoadProgressMap
  );

  const [translateState, setTranslateState] = useState(false);
  const [translatedFormData, setTranslatedFormData] = useState({});

  const handleRejectButton = (status) => {
    setLoader(true);
    rejectWeeklyReport(weeklyId, { status })
      .then(() => {
        toast.success('Weekly report rejected from IMSMA');
        setLoader(false);
        setTimeout(() => {
          navigate('/task-clearance/list');
        }, 2000);
      })
      .catch(() => {
        toast.error('Error rejecting Weekly report from IMSMA');
        setLoader(false);
      });
  };

  useEffect(() => {
    document.title = 'View weekly';
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const getWeeklyFormData = async () => {
      setLoader(true);
      try {
        const response = await axios.get(`/reports/clearance/weekly/${weeklyId}/`, { signal });
        setFormData(response.data);

        if (clearanceId) {
          try {
            const tcpResponse = await getTCPFormById(clearanceId);
            if (tcpResponse?.data?.ha_report) {
              const haResponse = await getHAById(tcpResponse?.data?.ha_report, false, false, signal);
              setZone(haResponse?.data?.file_zone);
              setHaReportPolygon(haResponse?.data?.info?.turn_points?.geojson);
            }
            if (tcpResponse?.data?.crosshair) {
              setCrosshair(tcpResponse.data.crosshair);
            }
          } catch (error) {
            if (error?.code === 'ERR_CANCELED') return;
            console.error('Failed to fetch zone from HA report:', error);
          }
        }
      } catch (error) {
        if (error?.code === 'ERR_CANCELED') return;
        console.error('Failed to fetch weekly data:', error);
      } finally {
        if (!signal.aborted) setLoader(false);
      }
    };

    if (weeklyId) {
      getWeeklyFormData();
    }
    return () => controller.abort();
  }, [weeklyId, clearanceId]);
  return (
    <section>
      <div className="flex-column-group">
        <TranslateWrapperComponent
          formData={{ ...formData, report_type: 'weekly_report' }}
          setTranslatedFormData={setTranslatedFormData}
          translationState={translateState}
        >
          <div className="text-hover flex-group flex-right" onClick={() => setTranslateState(!translateState)}>
            <TranslateSvg />
            <span>Translate</span>
          </div>
          <WeeklyViewForm
            formData={formData}
            commentsData={validationComments}
            mapLoader={mapLoader}
            gridGeojson={gridGeojson}
            setGridGeojson={setGridGeojson}
            translatedFormData={translatedFormData}
            translateState={translateState}
            user={user}
            handleRejectButton={handleRejectButton}
            zone={zone}
            haReportPolygon={haReportPolygon}
            crosshair={crosshair}
          />
        </TranslateWrapperComponent>
      </div>
      <Loader visible={loader} />
      <ToastContainer />
    </section>
  );
};

const WeeklyViewForm = ({
  formData,
  commentsData,
  mapLoader,
  gridGeojson,
  setGridGeojson,
  translatedFormData = {},
  translateState = false,
  user,
  handleRejectButton,
  zone,
  haReportPolygon,
  crosshair,
}) => (
  <div className="daily-form">
    <div className="form-container flex-column-group">
      <WeeklyFormHeader />
      <Accordion
        allowMultipleExpanded
        allowZeroExpanded
        preExpanded={['dms', 'fieldWork', 'marking', 'battle-area-clearance', 'eoFound']}
        className="custom-accordion"
      >
        <AccordionItem uuid="dms">
          <DMSBlockView
            formData={formData}
            // dispatch={dispatch}
            commentsData={commentsData}
            mode="view"
            translatedFormData={translatedFormData}
            translateState={translateState}
          />
        </AccordionItem>
        {(formData?.info?.dms?.team_activity === 'field_work' ||
          formData?.info?.dms?.team_activity?.includes('field_work')) && (
          <AccordionItem uuid="fieldWork">
            <FieldWorkBlockView
              formData={formData}
              // dispatch={dispatch}
              setGridGeojson={setGridGeojson}
              // coords3857={coords3857}
              zone={zone}
              gridGeojson={gridGeojson}
              loader={mapLoader}
              mode="view"
              commentsData={commentsData}
              translatedFormData={translatedFormData}
              translateState={translateState}
              haReportPolygon={haReportPolygon}
              crosshair={crosshair}
            />
          </AccordionItem>
        )}
      </Accordion>
      {formData?.validation_status?.value === 'validated'
        && user?.permissions?.reject_imsma_clearance_weekly
        && (
          <div className="form-buttons buttons-group py-10">
            <ButtonComponent
              handleButton={() => {
                handleRejectButton('rejected');
              }}
              label="Reject from IMSMA"
              disabled={false}
              className="danger"
              type="button"
            />
          </div>
        )}
    </div>
  </div>
);

const WeeklyFormHeader = () => (
  <div className="form-header">
    <span>View a weekly</span>
    <div className="line" />
  </div>
);

export default WeeklyViewPage;
